import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import moment from 'moment';
import { Observable, Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, finalize, switchMap, tap } from 'rxjs/operators';
import { exportTablePdf } from 'src/app/core/utils/export-table-pdf';
import { validarPermissao } from 'src/app/core/utils/permissionScreen/permissionScreen';
import { IOpcaoTela } from 'src/app/core/utils/permissionScreen/permissionScreen.types';
import { formatarData } from 'src/app/core/utils/utilities';
import { AppExportBase64 } from 'src/app/services/ExportBase64/exportBase64.service';
import {
  IEstoqueRelatorioFilter,
  IEstoqueRelatorioMovimentacaoListResponse,
  IEstoqueRelatorioMovimentacaoResumoProduto,
  IEstoqueRelatorioPosicaoItem,
  IInventarioItem
} from 'src/app/services/EstoqueService/estoque.model';
import { EstoqueService } from 'src/app/services/EstoqueService/estoque.service';
import { IProduto } from 'src/app/services/ProdutoService/produto.model';
import { ProdutoService } from 'src/app/services/ProdutoService/produto.service';
import { ReportService } from 'src/app/services/ReportService/report.service';
import {
  IGetRelatorioClientesCaixaItem,
  IGetRelatorioClientesCaixaListResponse
} from 'src/app/services/ReportService/report.model';
import {
  labelOrigemMovimentacao,
  MODO_PAGAMENTO_OPTIONS,
  ORIGEM_MOVIMENTACAO_OPTIONS,
  TIPO_MOVIMENTACAO_OPTIONS
} from '../../estoque.constants';

type ReportKey = 'inventario' | 'movimentacoes' | 'vendasEstoque' | 'posicao';

type ReportOption = {
  name: string;
  value: ReportKey;
  tela: IOpcaoTela;
};

type IOptions = { name: string; value: number | boolean | null };

@Component({
  selector: 'app-relatorio-estoque',
  templateUrl: './relatorio-estoque.component.html',
  styleUrls: ['./relatorio-estoque.component.scss']
})
export class RelatorioEstoqueComponent implements OnInit, OnDestroy {
  private readonly allReportOptions: ReportOption[] = [
    {
      name: 'Inventário',
      value: 'inventario',
      tela: 'listaInventario'
    },
    {
      name: 'Movimentações de Estoque',
      value: 'movimentacoes',
      tela: 'listaMovimentacoesEstoque'
    },
    {
      name: 'Vendas Estoque',
      value: 'vendasEstoque',
      tela: 'relatorioClientesCaixa'
    },
    {
      name: 'Posição de Estoque',
      value: 'posicao',
      tela: 'relatorioPosicaoEstoque'
    }
  ];

  /** Estado local — troca de relatório é síncrona e não dispara request. */
  reportType: ReportKey | null = null;

  form = this.fb.group({
    DT_INIC: [null as string | null],
    DT_FIM: [null as string | null],
    FK_PROD: [0 as number | null],
    NU_TIPO_MOVM: [0 as number | null],
    NU_ORIG_MOVM: [0 as number | null],
    FL_EM_ESTQ: [false],
    FL_ESTQ_BX: [false],
    PK_CAIX: [null as number | null],
    NM_FUNC: [null as string | null],
    NM_CLNT: [null as string | null],
    NU_TIPO_PGMT_CAIX: [null as number | null]
  });

  isLoading = false;
  tableData: Array<any> = [];
  tableColumns: Array<any> = [];
  resumoMovimentacoes: Array<any> = [];
  resumoMovimentacoesTotais: any = null;
  reportOptions: { name: string; value: ReportKey }[] = [];
  produtoOptions: IOptions[] = [{ name: 'Todos', value: 0 }];
  tipoMovimentacaoOptions = TIPO_MOVIMENTACAO_OPTIONS;
  origemMovimentacaoOptions = ORIGEM_MOVIMENTACAO_OPTIONS;
  formaPagamentoOptions: IOptions[] = [
    { name: 'Todos', value: null },
    ...MODO_PAGAMENTO_OPTIONS.map(o => ({ name: o.name, value: o.value }))
  ];

  valorTotal = 0;
  quantidadeTotal = 0;
  numeroPagina = 0;
  maxPages = 1;
  pesquisaParams = { PAGE_NUMBER: 1, PAGE_SIZE: 20 };
  totais = {
    qtInic: 0,
    vlInic: 0,
    qtSaid: 0,
    vlSaid: 0,
    qtAtual: 0,
    vlAtual: 0
  };

  private readonly loadTrigger$ = new Subject<void>();
  private readonly subscriptions = new Subscription();
  private produtosCarregados = false;
  private suppressFilterLoad = false;
  hasSearched = false;

  readonly resumoMovimentacoesColumns = [
    { header: 'Produto', key: 'produto', type: 'text' },
    { header: 'Saldo Anterior', key: 'saldoAnterior', type: 'text' },
    { header: 'Quantidade Entrada', key: 'qtdEntrada', type: 'text' },
    { header: 'Quantidade Vendas', key: 'qtdSaida', type: 'text' },
    { header: 'Saldo em Estoque', key: 'saldoEstoque', type: 'text' },
    { header: 'Valor Vendas', key: 'valorSaida', type: 'text' },
    { header: 'Valor Atual', key: 'valorAtual', type: 'text' }
  ];

  private readonly columnsByReport: Record<ReportKey, Array<any>> = {
    inventario: [
      { header: 'Produto', key: 'produto', type: 'text' },
      { header: 'Disponível', key: 'saldo', type: 'text' },
      { header: 'Vendida', key: 'vendida', type: 'text' },
      { header: 'Saldo Atual', key: 'saldoAtual', type: 'text' },
      { header: 'Valor Estoque', key: 'valorEstoque', type: 'text' },
      { header: 'Situação', key: 'situacao', type: 'text' }
    ],
    movimentacoes: [
      { header: 'Data', key: 'data', type: 'text' },
      { header: 'Código', key: 'codigo', type: 'text' },
      { header: 'Produto', key: 'produto', type: 'text' },
      { header: 'Origem', key: 'origem', type: 'text' },
      { header: 'Saldo Ant', key: 'saldoAnt', type: 'text' },
      { header: 'Qtd', key: 'quantidade', type: 'text' },
      { header: 'Saldo Após', key: 'saldoApos', type: 'text' },
      { header: 'Valor Entrada', key: 'valorEntrada', type: 'text' },
      { header: 'Valor Saída', key: 'valorSaida', type: 'text' },
      { header: 'Valor Após', key: 'valorApos', type: 'text' },
      { header: 'Usuário', key: 'usuario', type: 'text' }
    ],
    vendasEstoque: [
      { header: 'Nº Caixa', key: 'numeroCaixa', type: 'text' },
      { header: 'Data', key: 'data', type: 'text' },
      { header: 'Cliente', key: 'cliente', type: 'text' },
      { header: 'Produto', key: 'produto', type: 'text' },
      { header: 'Qtd', key: 'quantidade', type: 'text' },
      { header: 'Valor', key: 'valor', type: 'text' },
      { header: 'Forma de Pagamento', key: 'formaPagamento', type: 'text' },
      { header: 'Operador', key: 'operador', type: 'text' }
    ],
    posicao: [
      { header: 'Produto', key: 'produto', type: 'text' },
      { header: 'Estoque Inicial (Qtd)', key: 'qtInic', type: 'text' },
      { header: 'Estoque Inicial (R$)', key: 'vlInic', type: 'text' },
      { header: 'Saídas (Qtd)', key: 'qtSaid', type: 'text' },
      { header: 'Saídas (R$)', key: 'vlSaid', type: 'text' },
      { header: 'Saldo Atual (Qtd)', key: 'qtAtual', type: 'text' },
      { header: 'Saldo Atual (R$)', key: 'vlAtual', type: 'text' }
    ]
  };

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private estoqueService: EstoqueService,
    private produtoService: ProdutoService,
    private reportService: ReportService,
    private appExportBase64: AppExportBase64
  ) {
    this.form.patchValue(this.periodoMesAtual());
  }

  get isInventario(): boolean {
    return this.reportType === 'inventario';
  }

  get isVendasEstoque(): boolean {
    return this.reportType === 'vendasEstoque';
  }

  get isMovimentacoes(): boolean {
    return this.reportType === 'movimentacoes';
  }

  get isPosicao(): boolean {
    return this.reportType === 'posicao';
  }

  get reportTitle(): string {
    return this.allReportOptions.find(o => o.value === this.reportType)?.name ?? 'Selecione um relatório';
  }

  get valorTotalFormatado(): string {
    return this.valorTotal.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  }

  get totaisFormatados() {
    return {
      qtInic: this.totais.qtInic,
      vlInic: this.formatMoney(this.totais.vlInic),
      qtSaid: this.totais.qtSaid,
      vlSaid: this.formatMoney(this.totais.vlSaid),
      qtAtual: this.totais.qtAtual,
      vlAtual: this.formatMoney(this.totais.vlAtual)
    };
  }

  ngOnInit(): void {
    this.reportOptions = this.allReportOptions
      .filter(o => validarPermissao(o.tela, 'consultar'))
      .map(o => ({ name: o.name, value: o.value }));

    this.subscriptions.add(
      this.loadTrigger$
        .pipe(
          tap(() => {
            this.isLoading = true;
          }),
          switchMap(() =>
            this.fetchReportData$().pipe(
              catchError(() => {
                this.clearReportResults();
                return of(null);
              }),
              finalize(() => {
                this.isLoading = false;
              })
            )
          )
        )
        .subscribe()
    );

    // Aplica filtros automaticamente após a 1ª pesquisa (não inclui troca de relatório).
    this.subscriptions.add(
      this.form.valueChanges.pipe(debounceTime(350)).subscribe(() => {
        if (this.suppressFilterLoad || !this.reportType || !this.hasSearched) return;
        if (this.isVendasEstoque) {
          this.pesquisaParams.PAGE_NUMBER = 1;
          this.numeroPagina = 0;
        }
        this.loadData();
      })
    );

    const reportFromQuery = this.route.snapshot.queryParamMap.get('report') as ReportKey | null;
    const initialType =
      reportFromQuery && this.reportOptions.some(o => o.value === reportFromQuery)
        ? reportFromQuery
        : this.reportOptions.length === 1
          ? this.reportOptions[0].value
          : null;

    if (initialType) {
      this.onChangeReportType(initialType);
    }

    // Pré-carrega produtos em background (não bloqueia a UI).
    this.loadProdutos();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.loadTrigger$.complete();
  }

  onChangeReportType(value: ReportKey | null): void {
    this.suppressFilterLoad = true;
    this.reportType = value;
    this.clearReportResults();
    this.hasSearched = false;
    this.isLoading = false;
    this.maxPages = 1;
    this.numeroPagina = 0;
    this.pesquisaParams.PAGE_NUMBER = 1;
    this.totais = { qtInic: 0, vlInic: 0, qtSaid: 0, vlSaid: 0, qtAtual: 0, vlAtual: 0 };

    this.form.patchValue(
      {
        FK_PROD: 0,
        NU_TIPO_MOVM: 0,
        NU_ORIG_MOVM: 0,
        FL_EM_ESTQ: false,
        FL_ESTQ_BX: false,
        PK_CAIX: null,
        NM_FUNC: null,
        NM_CLNT: null,
        NU_TIPO_PGMT_CAIX: null
      },
      { emitEvent: false }
    );

    if (value) {
      this.tableColumns = this.columnsByReport[value];
      this.applyReportDefaults(value);
    } else {
      this.tableColumns = [];
    }

    setTimeout(() => {
      this.suppressFilterLoad = false;
    });
  }

  pesquisar(): void {
    if (this.isVendasEstoque) {
      this.pesquisaParams.PAGE_NUMBER = 1;
      this.numeroPagina = 0;
    }
    this.loadData();
  }

  toggleEmEstoque(value: boolean): void {
    this.form.controls.FL_EM_ESTQ.setValue(value);
  }

  toggleEstoqueBaixo(value: boolean): void {
    this.form.controls.FL_ESTQ_BX.setValue(value);
  }

  mudarPagina(pagina: number): void {
    this.pesquisaParams.PAGE_NUMBER = pagina + 1;
    this.numeroPagina = pagina;
    this.loadData();
  }

  private clearReportResults(): void {
    this.tableData = [];
    this.resumoMovimentacoes = [];
    this.resumoMovimentacoesTotais = null;
    this.valorTotal = 0;
    this.quantidadeTotal = 0;
  }

  private applyReportDefaults(_type: ReportKey): void {
    this.form.patchValue(this.periodoMesAtual(), { emitEvent: false });
  }

  private periodoMesAtual(): { DT_INIC: string; DT_FIM: string } {
    return {
      DT_INIC: moment().startOf('month').format('YYYY-MM-DDTHH:mm'),
      DT_FIM: moment().endOf('month').format('YYYY-MM-DDTHH:mm')
    };
  }

  private formatMoneyOrEmpty(value: number | null | undefined): string {
    if (value === null || value === undefined) return '';
    return this.formatMoney(value);
  }

  private mapResumoMovimentacoes(items: IEstoqueRelatorioMovimentacaoResumoProduto[]) {
    const rows = items.map(item => ({
      produto: item.NM_PROD,
      saldoAnterior: item.QT_SALD_ANT,
      qtdEntrada: item.QT_ENTR,
      qtdSaida: item.QT_SAID,
      saldoEstoque: item.QT_SALD_ATUAL,
      valorSaida: this.formatMoney(item.VL_SAID),
      valorAtual: this.formatMoney(item.VL_ATUAL)
    }));

    if (items.length) {
      const total = {
        produto: 'TOTAL',
        saldoAnterior: items.reduce((acc, i) => acc + Number(i.QT_SALD_ANT), 0),
        qtdEntrada: items.reduce((acc, i) => acc + Number(i.QT_ENTR), 0),
        qtdSaida: items.reduce((acc, i) => acc + Number(i.QT_SAID), 0),
        saldoEstoque: items.reduce((acc, i) => acc + Number(i.QT_SALD_ATUAL), 0),
        valorSaida: this.formatMoney(items.reduce((acc, i) => acc + Number(i.VL_SAID), 0)),
        valorAtual: this.formatMoney(items.reduce((acc, i) => acc + Number(i.VL_ATUAL), 0))
      };
      this.resumoMovimentacoes = [...rows, total];
      this.resumoMovimentacoesTotais = total;
    } else {
      this.resumoMovimentacoes = [];
      this.resumoMovimentacoesTotais = null;
    }
  }

  private loadProdutos(): void {
    if (this.produtosCarregados) return;
    this.produtoService.GetProdutos().subscribe({
      next: (produtos: IProduto[]) => {
        this.produtoOptions = [
          { name: 'Todos', value: 0 },
          ...produtos.map(p => ({ name: p.NM_PROD, value: p.PK_PROD }))
        ];
        this.produtosCarregados = true;
      }
    });
  }

  formatMoney(value: number): string {
    return Number(value).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  }

  private toOptionalNumber(value: unknown): number | undefined {
    const n = Number(value);
    return !Number.isNaN(n) && n > 0 ? n : undefined;
  }

  private createFilter(): IEstoqueRelatorioFilter {
    const filter: IEstoqueRelatorioFilter = {
      DT_INIC: this.normalizeDateParam(this.form.controls.DT_INIC.value),
      DT_FIM: this.normalizeDateParam(this.form.controls.DT_FIM.value, true)
    };

    const fkProd = this.toOptionalNumber(this.form.controls.FK_PROD.value);
    if (fkProd) filter.FK_PROD = fkProd;

    if (this.isMovimentacoes) {
      const tipo = this.toOptionalNumber(this.form.controls.NU_TIPO_MOVM.value);
      const origem = this.toOptionalNumber(this.form.controls.NU_ORIG_MOVM.value);
      if (tipo) filter.NU_TIPO_MOVM = tipo;
      if (origem) filter.NU_ORIG_MOVM = origem;
    }

    return filter;
  }

  /** Serializa datetime-local para o binder do ASP.NET; DT_FIM fecha o minuto com :59. */
  private normalizeDateParam(
    value: string | null | undefined,
    asEndOfMinute = false
  ): string | undefined {
    if (!value) return undefined;
    const m = moment(value, ['YYYY-MM-DDTHH:mm', 'YYYY-MM-DDTHH:mm:ss', moment.ISO_8601], true);
    if (!m.isValid()) {
      const fallback = moment(value);
      if (!fallback.isValid()) return undefined;
      return (asEndOfMinute ? fallback.seconds(59) : fallback).format('YYYY-MM-DDTHH:mm:ss');
    }
    return (asEndOfMinute ? m.clone().seconds(59) : m).format('YYYY-MM-DDTHH:mm:ss');
  }

  private getFormaPagamento(tipo: number | null | undefined): string {
    return MODO_PAGAMENTO_OPTIONS.find(o => o.value === tipo)?.name ?? '---';
  }

  loadData(): void {
    if (!this.reportType) return;

    const filter = this.createFilter();
    if (this.isPosicao && (!filter.DT_INIC || !filter.DT_FIM)) return;

    this.hasSearched = true;
    this.loadTrigger$.next();
  }

  private fetchReportData$(): Observable<unknown> {
    if (!this.reportType) {
      return of(null);
    }

    const filter = this.createFilter();
    if (this.isPosicao && (!filter.DT_INIC || !filter.DT_FIM)) {
      return of(null);
    }

    const type = this.reportType;

    if (type === 'inventario') {
      const flEmEstq = !!this.form.controls.FL_EM_ESTQ.value;
      const flEstqBx = !!this.form.controls.FL_ESTQ_BX.value;
      return this.estoqueService
        .GetInventario({
          DT_INIC: filter.DT_INIC,
          DT_FIM: filter.DT_FIM,
          FL_EM_ESTQ: flEmEstq ? true : undefined,
          FL_ESTQ_BX: flEstqBx ? true : undefined
        })
        .pipe(
          tap((items: IInventarioItem[]) => {
            this.valorTotal = items.reduce((acc, item) => acc + Number(item.VL_ESTQ), 0);
            this.tableData = items.map(item => ({
              produto: item.NM_PROD,
              saldo: item.QT_SALD,
              vendida: item.QT_VEND,
              saldoAtual: item.QT_SALD,
              valorEstoque: this.formatMoney(item.VL_ESTQ),
              situacao: item.TX_STTS_ESTQ
            }));
          })
        );
    }

    if (type === 'movimentacoes') {
      return this.estoqueService.GetRelatorioMovimentacoes(filter).pipe(
        tap((response: IEstoqueRelatorioMovimentacaoListResponse) => {
          const items = response.ITEMS ?? [];
          this.tableData = items.map(item => ({
            data: formatarData(item.DT_MOVM),
            codigo: item.PK_MOVM_ESTQ,
            produto: item.NM_PROD,
            origem: labelOrigemMovimentacao(item.NU_ORIG_MOVM),
            saldoAnt: item.QT_SALD_ANT,
            quantidade: item.QT_MOVM,
            saldoApos: item.QT_SALD_APOS,
            valorEntrada: this.formatMoneyOrEmpty(item.VL_ENTR),
            valorSaida: this.formatMoneyOrEmpty(item.VL_SAID),
            valorApos: this.formatMoney(item.VL_APOS ?? 0),
            usuario: item.NM_FUNC_OPER ?? '---'
          }));
          this.mapResumoMovimentacoes(response.RESUMO_PRODUTOS ?? []);
        })
      );
    }

    if (type === 'vendasEstoque') {
      const fkProd = this.toOptionalNumber(this.form.controls.FK_PROD.value);
      const tipoPgmt = this.toOptionalNumber(this.form.controls.NU_TIPO_PGMT_CAIX.value);
      const period = this.createFilter();
      return this.reportService
        .GetRelatorioClientesCaixa({
          PK_CAIX: this.toOptionalNumber(this.form.controls.PK_CAIX.value),
          NM_FUNC: this.form.controls.NM_FUNC.value ?? undefined,
          NM_CLNT: this.form.controls.NM_CLNT.value ?? undefined,
          FK_PROD: fkProd,
          NU_TIPO_PGMT_CAIX: tipoPgmt,
          DT_INIC: period.DT_INIC,
          DT_FIM: period.DT_FIM,
          PAGE_NUMBER: this.pesquisaParams.PAGE_NUMBER,
          PAGE_SIZE: this.pesquisaParams.PAGE_SIZE
        })
        .pipe(
          tap((response: IGetRelatorioClientesCaixaListResponse) => {
            this.tableData = (response.ITEMS ?? []).map((item: IGetRelatorioClientesCaixaItem) => ({
              numeroCaixa: item.PK_CAIX ?? '---',
              data: item.DT_PGMT_SAID ? formatarData(item.DT_PGMT_SAID) : '---',
              cliente: item.NM_CLNT ?? '---',
              produto: item.NM_PROD ?? '---',
              quantidade: Number(item.QT_VEND ?? 0),
              valor: this.formatMoney(Number(item.VL_VEND_ITEM ?? item.VL_TOTL ?? 0)),
              formaPagamento: this.getFormaPagamento(item.NU_TIPO_PGMT_CAIX),
              operador: item.NM_FUNC ?? '---'
            }));
            this.quantidadeTotal =
              response.QT_VEND_TOTL ??
              (response.RESUMO_PRODUTOS ?? []).reduce(
                (acc, r) => acc + Number(r.QUANTIDADE ?? 0),
                0
              );
            this.valorTotal =
              response.VL_PGMT_TOTL ??
              (response.RESUMO_PRODUTOS ?? []).reduce(
                (acc, r) => acc + Number(r.VALOR ?? 0),
                0
              );
            this.maxPages = response.TOTAL_PAGES;
          })
        );
    }

    return this.estoqueService.GetRelatorioPosicao(filter).pipe(
      tap((items: IEstoqueRelatorioPosicaoItem[]) => {
        this.totais = {
          qtInic: items.reduce((acc, i) => acc + Number(i.QT_SALD_INIC), 0),
          vlInic: items.reduce((acc, i) => acc + Number(i.VL_SALD_INIC), 0),
          qtSaid: items.reduce((acc, i) => acc + Number(i.QT_SAID), 0),
          vlSaid: items.reduce((acc, i) => acc + Number(i.VL_SAID), 0),
          qtAtual: items.reduce((acc, i) => acc + Number(i.QT_SALD_ATUAL), 0),
          vlAtual: items.reduce((acc, i) => acc + Number(i.VL_SALD_ATUAL), 0)
        };

        this.tableData = items.map(item => ({
          produto: item.NM_PROD,
          qtInic: item.QT_SALD_INIC,
          vlInic: this.formatMoney(item.VL_SALD_INIC),
          qtSaid: item.QT_SAID,
          vlSaid: this.formatMoney(item.VL_SAID),
          qtAtual: item.QT_SALD_ATUAL,
          vlAtual: this.formatMoney(item.VL_SALD_ATUAL)
        }));
      })
    );
  }

  exportarExcel(): void {
    if (!this.reportType) return;

    const filter = this.createFilter();
    if (this.isPosicao && (!filter.DT_INIC || !filter.DT_FIM)) return;

    this.isLoading = true;

    if (this.isVendasEstoque) {
      const fkProd = this.toOptionalNumber(this.form.controls.FK_PROD.value);
      const tipoPgmt = this.toOptionalNumber(this.form.controls.NU_TIPO_PGMT_CAIX.value);
      this.reportService
        .GetReportClientesCaixaExcel({
          PK_CAIX: this.form.controls.PK_CAIX.value ?? undefined,
          NM_FUNC: this.form.controls.NM_FUNC.value ?? undefined,
          NM_CLNT: this.form.controls.NM_CLNT.value ?? undefined,
          FK_PROD: fkProd,
          NU_TIPO_PGMT_CAIX: tipoPgmt,
          DT_INIC: this.form.controls.DT_INIC.value ?? undefined,
          DT_FIM: this.form.controls.DT_FIM.value ?? undefined
        })
        .subscribe({
          next: response => {
            this.appExportBase64.downloadExcel(response.ReportBase64, 'vendas-estoque');
            this.isLoading = false;
          },
          error: () => {
            this.isLoading = false;
          }
        });
      return;
    }

    const reportType =
      this.reportType === 'inventario'
        ? 'inventario'
        : this.reportType === 'movimentacoes'
          ? 'movimentacoes'
          : 'posicao';

    this.estoqueService
      .GetReportExcel({
        ReportType: reportType,
        DT_INIC: filter.DT_INIC,
        DT_FIM: filter.DT_FIM,
        FK_PROD: filter.FK_PROD,
        NU_TIPO_MOVM: filter.NU_TIPO_MOVM,
        NU_ORIG_MOVM: filter.NU_ORIG_MOVM,
        FL_EM_ESTQ: this.isInventario && this.form.controls.FL_EM_ESTQ.value ? true : undefined,
        FL_ESTQ_BX: this.isInventario && this.form.controls.FL_ESTQ_BX.value ? true : undefined
      })
      .subscribe({
        next: response => {
          this.appExportBase64.downloadExcel(response.ReportBase64, `${reportType}-estoque`);
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        }
      });
  }

  exportarPdfInventario(): void {
    exportTablePdf(
      'Inventário de Estoque',
      [
        { header: 'Produto', key: 'produto', width: 180 },
        { header: 'Disponível', key: 'saldo', width: 80 },
        { header: 'Vendida', key: 'vendida', width: 70 },
        { header: 'Saldo', key: 'saldoAtual', width: 70 },
        { header: 'Valor', key: 'valorEstoque', width: 100 },
        { header: 'Situação', key: 'situacao', width: 80 }
      ],
      this.tableData,
      'inventario-estoque',
      `Valor total do inventário: ${this.valorTotalFormatado}`
    );
  }

  exportarPdfMovimentacoes(): void {
    if (!this.isMovimentacoes) return;
    this.isLoading = true;
    const filter = this.createFilter();
    this.estoqueService.GetReportMovimentacoesPdf(filter).subscribe({
      next: response => {
        this.appExportBase64.downloadPDF(response.ReportBase64, 'movimentacoes-estoque');
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  exportarPdfVendasEstoque(): void {
    if (!this.isVendasEstoque) return;
    this.isLoading = true;
    const fkProd = this.toOptionalNumber(this.form.controls.FK_PROD.value);
    const tipoPgmt = this.toOptionalNumber(this.form.controls.NU_TIPO_PGMT_CAIX.value);
    this.reportService
      .GetReportClientesCaixaPdf({
        PK_CAIX: this.form.controls.PK_CAIX.value ?? undefined,
        NM_FUNC: this.form.controls.NM_FUNC.value ?? undefined,
        NM_CLNT: this.form.controls.NM_CLNT.value ?? undefined,
        FK_PROD: fkProd,
        NU_TIPO_PGMT_CAIX: tipoPgmt,
        DT_INIC: this.form.controls.DT_INIC.value ?? undefined,
        DT_FIM: this.form.controls.DT_FIM.value ?? undefined
      })
      .subscribe({
        next: response => {
          this.appExportBase64.downloadPDF(response.ReportBase64, 'vendas-estoque');
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        }
      });
  }

  exportarPdfPosicao(): void {
    if (!this.isPosicao) return;
    exportTablePdf(
      'Posição de Estoque',
      [
        { header: 'Produto', key: 'produto', width: 160 },
        { header: 'Est. Inicial (Qtd)', key: 'qtInic', width: 80 },
        { header: 'Est. Inicial (R$)', key: 'vlInic', width: 90 },
        { header: 'Saídas (Qtd)', key: 'qtSaid', width: 70 },
        { header: 'Saídas (R$)', key: 'vlSaid', width: 80 },
        { header: 'Atual (Qtd)', key: 'qtAtual', width: 70 },
        { header: 'Atual (R$)', key: 'vlAtual', width: 80 }
      ],
      this.tableData,
      'posicao-estoque',
      [
        `Inicial: ${this.totaisFormatados.qtInic} / ${this.totaisFormatados.vlInic}`,
        `Saídas: ${this.totaisFormatados.qtSaid} / ${this.totaisFormatados.vlSaid}`,
        `Atual: ${this.totaisFormatados.qtAtual} / ${this.totaisFormatados.vlAtual}`
      ].join(' | ')
    );
  }
}
